/* Public bulletin signature page — iter67.
 * Path: /sign-bulletin/:token — parent/teacher/director all use this page,
 * the backend infers the role from the token.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ShieldCheck, FileText, PenNib, CheckCircle, Buildings, ArrowClockwise,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABEL = {
  parent: 'Signature du parent',
  teacher: 'Signature du professeur principal',
  director: 'Signature de la Direction',
};

export default function SignBulletin() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('choose');   // choose | canvas | accept | done
  const [signedBy, setSignedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const loadMeta = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/public/bulletin/${token}`);
      setMeta(data);
      if (data.already_signed) setMode('done');
    } catch (e) {
      setError(e.response?.status === 404
        ? 'Bulletin introuvable ou lien invalide.'
        : 'Erreur de chargement.');
    } finally { setLoading(false); }
  };
  useEffect(() => { loadMeta(); }, [token]);

  const initCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#0F172A'; ctx.lineWidth = 2.5;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  };
  useEffect(() => { if (mode === 'canvas') { initCanvas(); setHasDrawn(false); } }, [mode]);

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * (c.width / rect.width), y: y * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true;
    const { x, y } = pos(e); const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const { x, y } = pos(e); const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y); ctx.stroke(); setHasDrawn(true);
  };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => { initCanvas(); setHasDrawn(false); };

  const submit = async (payload) => {
    if (!signedBy.trim() && meta?.role !== 'parent') {
      toast.error('Veuillez indiquer votre nom pour signer');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/bulletin/${token}/sign`, {
        ...payload, signed_by: signedBy.trim() || undefined,
      });
      toast.success('Signature enregistrée. Merci !');
      loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la signature');
    } finally { setBusy(false); }
  };
  const submitCanvas = () => {
    if (!hasDrawn) { toast.error('Veuillez dessiner votre signature'); return; }
    submit({ type: 'canvas', data: canvasRef.current.toDataURL('image/png') });
  };
  const submitAccept = () => submit({ type: 'accept' });

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR',
      { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052FF]" />
    </div>;
  }

  const roleLabel = meta ? (ROLE_LABEL[meta.role] || 'Signature') : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-white to-[#EFF6FF] py-6 px-4"
      data-testid="sign-bulletin-page" data-role={meta?.role}>
      <div className="max-w-2xl mx-auto">
        <Toaster position="top-center" richColors />
        <div className="flex items-center gap-2 mb-4 text-[#0052FF] px-1">
          <Buildings size={20} weight="duotone" />
          <span className="text-xs uppercase tracking-wide font-bold">{meta?.school_name || 'École'}</span>
        </div>

        {error ? (
          <div className="bg-white border border-red-200 rounded-2xl p-8 text-center shadow-sm">
            <div className="inline-flex w-14 h-14 rounded-full bg-red-100 text-red-600 items-center justify-center mb-3">
              <FileText size={28} weight="duotone" />
            </div>
            <h1 className="font-outfit text-xl font-bold text-red-700 mb-1">Lien invalide</h1>
            <p className="text-sm text-[#64748B]">{error}</p>
          </div>
        ) : meta ? (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0052FF] to-[#00A3FF] text-white px-6 py-5">
              <p className="text-[10px] uppercase tracking-wide opacity-90 font-bold">Bulletin scolaire</p>
              <h1 className="text-2xl font-outfit font-bold" data-testid="sign-student-name">
                {meta.student_name}
              </h1>
              <p className="text-xs opacity-90">
                {meta.class_name || '—'} · {meta.period_key} · {meta.school_year}
              </p>
              <p className="text-xs mt-2 bg-white/20 inline-block px-2 py-0.5 rounded"
                data-testid="sign-role-label">
                Rôle attendu : <b>{roleLabel}</b>
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Signatures status */}
              <div className="grid grid-cols-3 gap-2 text-xs">
                {Object.entries(meta.signatures_status || {}).map(([r, s]) => (
                  <div key={r} className={`p-2 rounded-lg border text-center ${
                    s ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-slate-50 border-slate-200 text-slate-500'
                  }`} data-testid={`status-${r}`}>
                    <p className="uppercase font-bold text-[9px]">{r}</p>
                    <p className="text-[10px] mt-0.5">{s ? '✓ Signé' : 'En attente'}</p>
                  </div>
                ))}
              </div>

              <a href={`${API}/api/public/bulletin/${token}/pdf`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0052FF] hover:underline"
                data-testid="sign-open-pdf-link">
                <FileText size={16} weight="duotone" /> Consulter le bulletin PDF
              </a>

              {/* Already signed for this role */}
              {mode === 'done' || meta.already_signed ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center"
                  data-testid="sign-done-block">
                  <div className="inline-flex w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center mb-2">
                    <ShieldCheck size={32} weight="fill" />
                  </div>
                  <h2 className="font-outfit text-lg font-bold text-emerald-800 mb-1">
                    Votre signature a été enregistrée
                  </h2>
                  <p className="text-sm text-emerald-800/80">
                    {meta.signed
                      ? 'Le bulletin est intégralement signé (3/3).'
                      : 'Les autres signataires seront relancés automatiquement.'}
                  </p>
                </div>
              ) : (
                <>
                  {meta.role !== 'parent' && (
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-[#64748B]">
                        Votre nom (obligatoire)
                      </label>
                      <Input value={signedBy} onChange={(e) => setSignedBy(e.target.value)}
                        placeholder="Nom Prénom" data-testid="signed-by-input" />
                    </div>
                  )}
                  {mode === 'choose' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                      <button type="button" onClick={() => setMode('canvas')}
                        className="p-5 rounded-xl border-2 border-[#0052FF] bg-blue-50 text-left hover:bg-blue-100"
                        data-testid="sign-choose-canvas-btn">
                        <PenNib size={26} weight="duotone" className="text-[#0052FF] mb-1" />
                        <p className="font-outfit font-bold text-sm">Signature manuscrite</p>
                        <p className="text-xs text-[#64748B]">Dessin au doigt ou à la souris.</p>
                      </button>
                      <button type="button" onClick={() => setMode('accept')}
                        className="p-5 rounded-xl border-2 border-emerald-500 bg-emerald-50 text-left hover:bg-emerald-100"
                        data-testid="sign-choose-accept-btn">
                        <CheckCircle size={26} weight="duotone" className="text-emerald-600 mb-1" />
                        <p className="font-outfit font-bold text-sm">J&apos;accepte et signe</p>
                        <p className="text-xs text-[#64748B]">Signature électronique horodatée.</p>
                      </button>
                    </div>
                  )}
                  {mode === 'canvas' && (
                    <div className="pt-2 space-y-3">
                      <div className="border-2 border-dashed border-[#0052FF] rounded-xl bg-white overflow-hidden touch-none">
                        <canvas ref={canvasRef} width={600} height={200}
                          className="w-full h-40 cursor-crosshair block"
                          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                          data-testid="sign-canvas" />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setMode('choose')}>Retour</Button>
                        <Button variant="outline" onClick={clearCanvas}>
                          <ArrowClockwise size={14} className="mr-1" /> Effacer
                        </Button>
                        <Button onClick={submitCanvas} disabled={busy || !hasDrawn}
                          className="bg-[#0052FF] hover:bg-[#0044D4]"
                          data-testid="sign-canvas-submit-btn">
                          <PenNib size={14} className="mr-1" />
                          {busy ? 'Enregistrement…' : 'Confirmer'}
                        </Button>
                      </div>
                    </div>
                  )}
                  {mode === 'accept' && (
                    <div className="pt-2 space-y-3">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <p className="text-sm text-emerald-900">
                          En cliquant sur <b>J&apos;accepte et signe</b>, vous reconnaissez avoir pris
                          connaissance du bulletin ci-dessus. Un horodatage électronique sera enregistré.
                        </p>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setMode('choose')}>Retour</Button>
                        <Button onClick={submitAccept} disabled={busy}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="sign-accept-submit-btn">
                          <CheckCircle size={14} className="mr-1" weight="fill" />
                          {busy ? 'Enregistrement…' : "J'accepte et signe"}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {fmtDate && null}
            </div>
            <div className="border-t border-[#E2E8F0] px-6 py-3 text-[10px] text-[#94A3B8] text-center">
              Lien de signature sécurisé — chaque signataire dispose d&apos;un jeton unique.
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
